import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CurrentBrandScope } from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  type IBreadcrumb,
  InputSearchFilter,
  toSearchStream,
  TrackByFunctions,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  Brand,
  RevokeAccessFromDisabledStaff,
} from '@principle-theorem/principle-core';
import { type IStaffer } from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'pr-staff-list',
  templateUrl: './staff-list.component.html',
  styleUrls: ['./staff-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StaffListComponent {
  trackByTeam = TrackByFunctions.ref<WithRef<IStaffer>>();
  searchCtrl: TypedFormControl<string> = new TypedFormControl<string>('');
  staffFilter: InputSearchFilter<WithRef<IStaffer>>;
  breadcrumbs$: Observable<IBreadcrumb[]>;
  emptyState$: Observable<boolean>;
  staff$: Observable<WithRef<IStaffer>[]>;

  constructor(
    private _brandScope: CurrentBrandScope,
    private _basicDialog: BasicDialogService
  ) {
    const brand$ = this._brandScope.doc$.pipe(filterUndefined());
    this.staff$ = brand$.pipe(
      switchMap((brand) => Brand.staff$(brand)),
      map((staff) => sortBy(staff, ['user.name']))
    );
    this.breadcrumbs$ = brand$.pipe(
      map((brand) => [
        { label: 'Settings', path: '../../../' },
        { label: brand.name },
        { label: 'Staff' },
      ])
    );

    this.staffFilter = new InputSearchFilter(
      this.staff$,
      toSearchStream(this.searchCtrl),
      ['user.name']
    );

    this.emptyState$ = this.staff$.pipe(map((staff) => !staff.length));
  }

  async removeDisabledStaff(): Promise<void> {
    const confirmed = await this._basicDialog.confirm({
      prompt: 'This action can not be undone. Continue?',
      title: 'Revoke Access From Disabled Staff',
      submitColor: 'warn',
      submitLabel: 'Continue',
    });
    if (!confirmed) {
      return;
    }

    const staff = await snapshot(this.staff$);
    await new RevokeAccessFromDisabledStaff().run(staff);
  }
}
